import { PlusIcon } from "@heroicons/react/outline";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGroupMappingsColumns } from "./GroupMappings/useGroupMappingsColumns";
import { GroupMappingTableRow } from "./GroupMappings/types";
import { computeCompactDataTableHeight } from "./compactDataTableHeight";
import { SectionShell } from "./SectionShell";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/ag-grid-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableColumn } from "@/components/ui/TableV2";
import { ToastType } from "@/enums";
import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import { useWorkspaces } from "@/queries";
import {
  useCreateGroupMapping,
  useDeleteGroupMapping,
  useDirectoryGroups,
  useGroupMappings,
} from "@/queries/identity/useGroupMappings";
import { useWorkspaceRoles } from "@/queries/identity/useWorkspaceRoles";
import type { Workspace } from "@/types/workspaces";
import { displayToast } from "@/utils/toast";

const ASSIGNABLE_GROUP_MAPPING_ROLES = new Set(["Admin", "Contributor", "Publisher", "Developer"]);

interface Props {
  organizationId: number;
  onError: (msg: string | null) => void;
}

const GROUP_MAPPINGS_ROW_HEIGHT = 48;

/** IdP group → workspace role mappings. Each row grants members of an IdP group a role on a workspace. */
export const GroupMappingsSection = ({ organizationId, onError }: Props) => {
  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const { data: groupsData, isLoading: groupsLoading } = useDirectoryGroups(organizationId);
  const { data: mappingsData, isLoading: mappingsLoading } = useGroupMappings(organizationId);
  const { data: workspacesData } = useWorkspaces();
  const createMapping = useCreateGroupMapping(organizationId);
  const deleteMapping = useDeleteGroupMapping(organizationId);
  const { columns } = useGroupMappingsColumns();

  const [showForm, setShowForm] = useState(false);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const [assignedRole, setAssignedRole] = useState("");

  const { data: rolesData } = useWorkspaceRoles(workspaceId);

  const assignableRoles = useMemo(
    () => (rolesData?.roles ?? []).filter(role => ASSIGNABLE_GROUP_MAPPING_ROLES.has(role.name)),
    [rolesData],
  );

  useEffect(() => {
    if (!assignedRole) {
      return;
    }
    if (!rolesData) {
      return;
    }
    const isStillValid = assignableRoles.some(role => role.name === assignedRole);
    if (!isStillValid) {
      setAssignedRole("");
    }
  }, [assignableRoles, assignedRole, rolesData]);

  const orgWorkspaces = useMemo<Workspace[]>(() => {
    const list = workspacesData?.workspaces;
    if (!list) {
      return [];
    }
    return list.filter(w => w.organization_id === organizationId);
  }, [workspacesData, organizationId]);

  const groupNameById = useMemo(() => {
    const map = new Map<number, string>();
    (groupsData?.groups ?? []).forEach(group => map.set(group.id, group.name));
    return map;
  }, [groupsData?.groups]);

  const workspaceNameById = useMemo(() => {
    const map = new Map<number, string>();
    orgWorkspaces.forEach(workspace => map.set(workspace.id, workspace.name));
    return map;
  }, [orgWorkspaces]);

  const tableRows = useMemo<GroupMappingTableRow[]>(
    () =>
      (mappingsData?.mappings ?? []).map(mapping => ({
        ...mapping,
        idp_group_name:
          groupNameById.get(mapping.directory_group_id) ?? `Group #${mapping.directory_group_id}`,
        workspace_name:
          workspaceNameById.get(mapping.workspace_id) ?? `Workspace #${mapping.workspace_id}`,
      })),
    [groupNameById, mappingsData?.mappings, workspaceNameById],
  );

  const resetForm = () => {
    setGroupId(null);
    setWorkspaceId(null);
    setAssignedRole("");
    setShowForm(false);
  };

  const handleCreate = () => {
    if (groupId === null || workspaceId === null || !assignedRole) {
      onError("Pick a group, a workspace, and a role.");
      return;
    }
    onError(null);
    createMapping.mutate(
      {
        directory_group_id: groupId,
        workspace_id: workspaceId,
        assigned_role: assignedRole,
      },
      {
        onSuccess: () => {
          displayToast("Mapping created.", ToastType.success);
          resetForm();
        },
        onError: () => onError("Failed to create mapping."),
      },
    );
  };

  const handleDelete = useCallback(
    (row?: GroupMappingTableRow) => {
      if (!row) {
        return;
      }
      onError(null);
      deleteMapping.mutate(row.id, {
        onSuccess: () => displayToast("Mapping deleted.", ToastType.success),
        onError: () => onError("Failed to delete mapping."),
      });
    },
    [deleteMapping, onError],
  );

  const hasGroups = (groupsData?.groups.length ?? 0) > 0;
  const isLoading = groupsLoading || mappingsLoading;
  const tableHeightPx = computeCompactDataTableHeight({
    rowCount: tableRows.length,
    rowHeight: GROUP_MAPPINGS_ROW_HEIGHT,
    includeToolbar: true,
  });

  return (
    <SectionShell
      title="Group mappings"
      description="Map IdP groups to workspace roles. Synced on every SCIM update."
    >
      {!groupsLoading && !hasGroups && (
        <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-600">
          No directory groups synced yet. Open the Directory Sync Admin Portal above to connect your
          IdP — groups appear here automatically after the first sync.
        </div>
      )}

      {showForm && hasGroups && (
        <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">IdP group</label>
              <Select
                value={groupId !== null ? String(groupId) : ""}
                onValueChange={v => setGroupId(Number(v))}
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue placeholder="Pick a group" />
                </SelectTrigger>
                <SelectContent>
                  {groupsData!.groups.map(g => (
                    <SelectItem key={g.id} value={String(g.id)}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Workspace</label>
              <Select
                value={workspaceId !== null ? String(workspaceId) : ""}
                onValueChange={v => {
                  setWorkspaceId(Number(v));
                  setAssignedRole("");
                }}
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue placeholder="Pick a workspace" />
                </SelectTrigger>
                <SelectContent>
                  {orgWorkspaces.map(w => (
                    <SelectItem key={w.id} value={String(w.id)}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Workspace role</label>
              <Select
                value={assignedRole || ""}
                onValueChange={setAssignedRole}
                disabled={workspaceId === null || assignableRoles.length === 0}
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue
                    placeholder={workspaceId === null ? "Pick a workspace first" : "Pick a role"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {assignableRoles.map(role => (
                    <SelectItem key={role.id} value={role.name}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={createMapping.isPending}>
              Create mapping
            </Button>
            <Button variant="ghost" onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {hasGroups && (
        <div className="w-full overflow-hidden" style={{ height: tableHeightPx }}>
          <DataTable<GroupMappingTableRow>
            data={isLoading ? [] : tableRows}
            columns={columns as TableColumn<GroupMappingTableRow>[]}
            isLoading={isLoading}
            height="100%"
            rowHeight={GROUP_MAPPINGS_ROW_HEIGHT}
            getRowHeight={() => GROUP_MAPPINGS_ROW_HEIGHT}
            hoverable
            enableSorting={false}
            rowsDeletable
            objectName="mapping"
            deleteConfirmText="Delete mapping"
            deleteConfirmSaveVariant="destructive"
            onRequestDeleteConfirm={onRequestDeleteConfirm}
            onDelete={handleDelete}
            emptyStateMessage="No group mappings yet. Add a mapping to grant workspace roles via SCIM groups."
            action={
              <Button
                variant="outline"
                onClick={() => setShowForm(s => !s)}
                disabled={!hasGroups}
                className="gap-2"
              >
                <PlusIcon className="h-4 w-4" /> Add mapping
              </Button>
            }
            gridOptions={{
              suppressHorizontalScroll: false,
              alwaysShowHorizontalScroll: false,
              suppressScrollOnNewData: true,
            }}
            className="[&_.ag-cell-value]:justify-start [&_.ag-header-cell-label]:justify-start [&_.ag-header-cell-text]:text-left"
          />
        </div>
      )}
    </SectionShell>
  );
};
